import { ReactNode, useCallback, useEffect, useState, type CSSProperties } from 'react';
import { SeekMobileBottomNav, SeekSidebar } from './SeekSidebar';
import { SidebarProvider } from '@/components/ui/sidebar';
import { useDirection } from '@/hooks/useDirection';
import { ProfileIncompleteBanner } from './ProfileIncompleteBanner';
import { useTheme } from 'next-themes';
import { useTranslation } from 'react-i18next';

const seekLogoAssets = {
  en: {
    light: '/SVG-New/Seek-Black%20En.svg',
    dark: '/SVG-New/Seek-White%20En.svg',
  },
  ar: {
    light: '/SVG-New/Seek-Black%20Ar.svg',
    dark: '/SVG-New/Seek-White%20Ar.svg',
  },
} as const;

const SEEK_SIDEBAR_STORAGE_KEY = 'forkseek:sidebar-open';
const SEEK_SIDEBAR_RAIL_STYLE = { '--sidebar-width-icon': '4rem' } as CSSProperties;

export function SeekLayout({ children }: { children: ReactNode }) {
  const { dir } = useDirection();
  const { theme, resolvedTheme } = useTheme();
  const { i18n } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const isArabic = (i18n.resolvedLanguage ?? i18n.language)?.startsWith('ar');
  const isDark = (resolvedTheme ?? theme) === 'dark';
  const seekLogoSrc = seekLogoAssets[isArabic ? 'ar' : 'en'][isDark ? 'dark' : 'light'];

  useEffect(() => {
    const storedValue = window.localStorage.getItem(SEEK_SIDEBAR_STORAGE_KEY);
    if (storedValue !== null) {
      setSidebarOpen(storedValue === 'true');
    }
  }, []);

  const handleSidebarOpenChange = useCallback((open: boolean) => {
    setSidebarOpen(open);
    window.localStorage.setItem(SEEK_SIDEBAR_STORAGE_KEY, String(open));
  }, []);

  return (
    <SidebarProvider open={sidebarOpen} onOpenChange={handleSidebarOpenChange} style={SEEK_SIDEBAR_RAIL_STYLE}>
      <div dir={dir} className="seek-theme seek-app-frame flex h-dvh w-full overflow-hidden">
        <SeekSidebar />
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <header
            className="h-16 flex items-center justify-center border-b border-border/80 px-3 sm:px-4 bg-card/[0.92] backdrop-blur-md sticky top-0 z-30 md:hidden safe-area-inset shadow-sm"
          >
            <img src={seekLogoSrc} alt="Seek" className="h-auto w-28 max-w-[44vw] shrink-0" />
          </header>
          <main className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-3 pb-[calc(7rem+env(safe-area-inset-bottom))] pt-4 sm:px-5 sm:pt-5 md:px-6 md:py-6 lg:px-8 lg:py-8 xl:px-10 3xl:px-14 4xl:px-16">
            <ProfileIncompleteBanner />
            {children}
          </main>
        </div>
        <SeekMobileBottomNav />
      </div>
    </SidebarProvider>
  );
}
